/**
 * «Одно нажатие»: на поле два цикла — к какому отнести работу (№1036–1039).
 *
 * Ядро (`cycle_for`) не выбирает цикл молча, когда окна двух циклов содержат
 * дату работы (пшеница и хлопок в апреле–июне). Подсказка «открыта кампания
 * этого вида» срабатывает на сервере сама; сюда приходит только неочевидное.
 * Решает менеджер или фермер — кто первым; право проверяет сервер.
 *
 * Компонент общий для разбора воды (разрешено «полил обе культуры» — полив
 * делится по доле посева) и очереди карантина «вне агроцикла» (один цикл на
 * задачу). Варианты и их порядок — от сервера; клиент ничего не считает.
 */
import { useState } from 'react';
import { haUnit } from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  group: {
    id: 'common.ui.cycleChoice.aria',
    defaultMessage: 'Выбор агроцикла: на поле два агроцикла',
    description: 'aria-label группы кнопок выбора агроцикла (разбор полива, карантин): на поле одновременно два агроцикла',
  },
  question: {
    id: 'common.ui.cycleChoice.question',
    defaultMessage: 'На поле два агроцикла — к какому отнести?',
    description: 'Вопрос перед кнопками выбора агроцикла работы или полива (менеджер, фермер)',
  },
  openCampaign: {
    id: 'common.ui.cycleChoice.openCampaign',
    defaultMessage: 'у агроцикла открыта кампания этого вида работ',
    description: 'Подсказка на кнопке агроцикла: у него уже идёт кампания того же вида работ',
  },
  sown: {
    id: 'common.ui.cycleChoice.sown',
    defaultMessage: ' · посев {area}',
    description: 'Хвост подписи кнопки агроцикла: площадь посева культуры; {area} — гектары с единицей; начинается с « · »',
  },
  splitTitle: {
    id: 'common.ui.cycleChoice.splitTitle',
    defaultMessage: 'полив делится между культурами по доле посева',
    description: 'Подсказка кнопки «Обе культуры» в разборе полива',
  },
  split: {
    id: 'common.ui.cycleChoice.split',
    defaultMessage: 'Обе культуры',
    description: 'Кнопка разбора полива: отнести полив к обеим культурам поля; коротко',
  },
});

export type CycleOption = {
  cycle_id: number;
  name: string;
  sown_microha: number | null;
  open_campaign: boolean;
};

export type CycleChoice = { cycle_id: number } | { split: true };

export function CycleChoiceButtons({
  options,
  allowSplit = false,
  onChoose,
}: {
  options: readonly CycleOption[];
  allowSplit?: boolean;
  /** Записать выбор; `false` — отказ сервера (текст покажет вызывающий). */
  onChoose: (choice: CycleChoice) => Promise<boolean>;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  if (options.length < 2) return null;
  const choose = async (choice: CycleChoice) => {
    setBusy(true);
    try {
      await onChoose(choice);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="cycle-choice"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
      role="group"
      aria-label={t(m.group)}
    >
      <span className="t-caption">{t(m.question)}</span>
      {options.map((o) => (
        <button
          key={o.cycle_id}
          type="button"
          className={`btn btn--sm focus-ring ${o.open_campaign ? 'btn--primary' : 'btn--secondary'}`}
          disabled={busy}
          title={o.open_campaign ? t(m.openCampaign) : undefined}
          onClick={() => choose({ cycle_id: o.cycle_id })}
        >
          {o.name}
          {o.sown_microha != null ? t(m.sown, { area: haUnit(o.sown_microha / 1e6) }) : ''}
        </button>
      ))}
      {allowSplit ? (
        <button
          type="button"
          className="btn btn--secondary btn--sm focus-ring"
          disabled={busy}
          title={t(m.splitTitle)}
          onClick={() => choose({ split: true })}
        >
          {t(m.split)}
        </button>
      ) : null}
    </div>
  );
}
